import { leadInfo } from './variables';
import { endLeadSession } from '../services/leadServices';

/**
 * Session watch: collect drop-off leads into the CRM immediately instead of
 * waiting for the backend EndWidgetSessions cron's 5-minute idle sweep.
 *
 * Rules:
 *  - Widget bubble closed OR tab hidden for GRACE_MS (15s) → end the session.
 *    Reopening the widget / returning to the tab within the grace cancels it.
 *  - Page unload (pagehide) → end the session immediately via fetch keepalive
 *    (no grace is possible — the page is going away).
 *
 * Only fires when a lead exists for this page load (leadInfo is set), i.e.
 * the visitor actually engaged with the widget. The backend endpoint is a
 * no-op when the session is already ended, and a later step call resets
 * session_ended=false if the visitor resumes — so over-firing is harmless.
 * The 5-minute cron remains the backstop for killed tabs / dead network
 * where no beacon can fire.
 */
const GRACE_MS = 15000;

let timer = null;

function activeLeadId() {
    return leadInfo?.id || null;
}

function fire() {
    const id = activeLeadId();
    if (id) {
        endLeadSession(id);
    }
}

function schedule() {
    if (timer || !activeLeadId()) return;
    timer = setTimeout(() => {
        timer = null;
        fire();
    }, GRACE_MS);
}

function cancel() {
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
}

/** Parent page closed the widget bubble (REFRESH message). */
export function sessionWatchWidgetClosed() {
    schedule();
}

/** Parent page opened the widget bubble (WIDGET_OPEN message). */
export function sessionWatchWidgetOpened() {
    cancel();
}

export function initSessionWatch() {
    // Tab hidden/visible. Note: mobile browsers may suspend timers while
    // backgrounded — if the tab never comes back the cron backstop catches it.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            schedule();
        } else {
            cancel();
        }
    });

    // Real page unload (navigation / tab close): no grace possible, beacon now.
    window.addEventListener('pagehide', () => {
        cancel();
        fire();
    });
}
